import { useNavigate } from 'react-router-dom';
import { Megaphone,  Tag, Calendar } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { noticesApi } from '@/features/teacher/api/notices.api';
import { NOTICE_TYPES } from '@/features/teacher/types/notices';
import { useApiQuery } from '@/hooks/useApiQuery';

/** How many fit the card before it becomes a list nobody reads on the way past. */
const NOTICES_SHOWN = 3;

/**
 * A compact overview-page preview of the teacher's class notices.
 *
 * Reads its own rows rather than being handed them, the way the institute's
 * notices card beside it does: the board and this card then always agree, and
 * the dashboard does not carry state for a panel it only draws.
 */
export function TeacherNoticesCard() {
  const navigate = useNavigate();
  // Published only: a draft is something the teacher has not finished writing,
  // and the overview is a preview of what the class can actually see.
  const { data, isLoading, error, refetch } = useApiQuery(
    () => noticesApi.list({ status: 'PUBLISHED' }),
    [],
  );

  const notices = data?.notices ?? [];
  const preview = notices.slice(0, NOTICES_SHOWN);

  return (
    <div className="card overflow-hidden bg-surface border border-line rounded-[16px] shadow-2xs">
      {/* Card Header */}
      <div className="border-b border-line bg-surface px-5 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-indigo-600 dark:text-indigo-400">
            <Megaphone className="h-4.5 w-4.5" />
          </span>
          <h2 className="text-base font-bold text-ink tracking-tight">
            My Class Notices
          </h2>
          {notices.length > 0 && (
            <span className="text-2xs font-bold text-white bg-indigo-600 px-2 py-0.5 rounded-full shadow-2xs">
              {notices.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/teacher/notices')}
          className="text-sm font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 transition-colors"
        >
          View all
        </button>
      </div>

      {/* Preview List */}
      <div className="p-4 flex flex-col gap-3">
        {isLoading && <SkeletonLines count={3} />}

        {!isLoading && error && (
          <div className="flex flex-col items-start gap-3">
            <Alert tone="critical" title="Could not load your notices">
              {error.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={refetch}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && (preview.length === 0 ? (
          <div className="py-6 text-center flex flex-col items-center gap-2.5">
            <div className="h-10 w-10 rounded-[12px] bg-indigo-500/10 border border-indigo-500/20 grid place-items-center">
              <Megaphone className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-ink-2">No notices posted yet</p>
              <p className="text-xs text-ink-3 font-medium mt-0.5">
                Click "Add Notice" to inform your students.
              </p>
            </div>
          </div>
        ) : (
          preview.map((notice) => {
            const ts = NOTICE_TYPES[notice.noticeType];
            return (
              <div
                key={notice.uuid}
                className="p-2 rounded-[10px] border border-line bg-surface hover:bg-slate-50/70 dark:hover:bg-surface-2 transition-colors flex flex-col gap-1.5"
              >
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${ts.dot}`} />
                  <span className="text-[14px] font-bold text-ink leading-snug line-clamp-1 [word-spacing:0.06em]">
                    {notice.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span
                    className={`text-2xs font-bold px-1.5 py-0.5 rounded-pill border ${ts.color} ${ts.bg} ${ts.border}`}
                  >
                    {ts.label}
                  </span>
                  <span className="flex items-center gap-1 text-[12px] text-ink-3 font-medium">
                    <Tag className="h-2.5 w-2.5" />
                    {notice.targetClass}
                  </span>
                  <span className="flex items-center gap-1 text-2xs text-ink-3 font-medium ml-auto">
                    <Calendar className="h-2.5 w-2.5" />
                    {new Date(notice.noticeDate + 'T00:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            );
          })
        ))}

        {/* View All link */}
        {/* {notices.length > NOTICES_SHOWN && (
          <button
            type="button"
            onClick={() => navigate('/teacher/notices')}
            className="flex items-center justify-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 py-2 rounded-[10px] border border-indigo-200 dark:border-indigo-500/20 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all"
          >
            View all {notices.length} notices
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )} */}
      </div>
    </div>
  );
}
